import React, { useEffect } from "react";
import styles from "./styles.module.css";
import { Grid } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useTranslation } from "react-i18next";
import Select from "@/components/atoms/Select";
import { useContractStore } from "@/stores/contractStore";
import ContractSortTypeEnum from "@/constants/enums/ContractSortTypeEnum";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import { enumToStringArray } from "@/utils/enumToArray";
import SortButton from "@/components/atoms/SortButton";
import { useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import CTA from "@/components/atoms/CTA";
import { useContractBulkEditStore } from "@/stores/contractBulkEditStore";
import TextInput from "@/components/atoms/TextInput";

const ContractsFilter = () => {
  const setSortOption = useContractStore((state) => state.setSortOption);
  const setStatuses = useContractStore((state) => state.setContractStatuses);
  const statuses = useContractStore((state) => state.statuses);
  const sortOption = useContractStore((state) => state.sortOption);
  const sortDirection = useContractStore((state) => state.sortDirection);
  const query = useContractStore((state) => state.query);
  const setQuery = useContractStore((state) => state.setQuery);
  const reset = useContractStore((state) => state.reset);
  const setSortDirection = useContractStore((state) => state.setSortDirection);
  const setEditMode = useContractBulkEditStore((state) => state.setEditMode);
  const editMode = useContractBulkEditStore((state) => state.editMode);
  const selectedRows = useContractBulkEditStore((state) => state.selectedRows);
  const clearSelection = useContractBulkEditStore((state) => state.clearSelection);
  const openModal = useContractBulkEditStore((s) => s.openModal);

  const { t } = useTranslation();

  /* Get sort and filter options from URL or session storage */
  const [searchParams, setSearchParams] = useSearchParams();
  const [debouncedQuery] = useDebouncedValue(query, 400);

  useEffect(() => {
    const KEY = "contracts_filters_v1";
    const q = searchParams.get("q");
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    const statusParams = searchParams.getAll("status");

    const hasParams = Boolean(q || sort || dir || (statusParams && statusParams.length > 0));

    let paramsToUse = searchParams;

    if (!hasParams) {
      try {
        const saved = sessionStorage.getItem(KEY);
        if (saved) {
          const savedSP = new URLSearchParams(saved);
          paramsToUse = savedSP as unknown as URLSearchParams; // use for initialization below
          setSearchParams(savedSP, { replace: true });
        }
      } catch {
        // ignored
      }
    }

    const q2 = paramsToUse.get("q");
    const sort2 = paramsToUse.get("sort");
    const dir2 = paramsToUse.get("dir");
    const statusParams2 = paramsToUse.getAll("status");

    if (q2 !== null && q2 !== undefined) {
      setQuery(q2);
    }
    if (sort2) {
      setSortOption(sort2 as ContractSortTypeEnum);
    }
    if (dir2) {
      const dd = dir2.toUpperCase() === "DESC" ? "DESC" : "ASC";
      setSortDirection(dd as "ASC" | "DESC");
    }
    if (statusParams2 && statusParams2.length > 0) {
      setStatuses(statusParams2 as ContractStatusEnum[]);
    } else {
      setStatuses([]);
    }
  }, []);

  /* Set sort and filter options to URL or session storage */
  useEffect(() => {
    const KEY = "contracts_filters_v1";
    const sp = new URLSearchParams();
    if (debouncedQuery) sp.set("q", debouncedQuery);
    if (sortOption) {
      sp.set("sort", sortOption);
    }
    if (sortDirection) sp.set("dir", sortDirection.toLowerCase());
    if (statuses && statuses.length > 0) {
      statuses.forEach((s) => sp.append("status", s));
    }

    setSearchParams(sp, { replace: true });

    try {
      sessionStorage.setItem(KEY, sp.toString());
    } catch {
      // ignore
    }
  }, [debouncedQuery, sortOption, sortDirection, statuses, setSearchParams]);

  const sortOptions: Array<{ value: string; label: string }> = enumToStringArray(ContractSortTypeEnum).map(
    (option: string) => {
      return {
        value: option,
        label: t("commonComponents:sortByX", {
          field: t(`enumerables:contractSortOption.${option}`),
        }),
      };
    }
  );

  const statusOptions = enumToStringArray(ContractStatusEnum);

  const changeDirection = () => {
    const sortDirection = useContractStore.getState().sortDirection;
    const setSortDirection = useContractStore.getState().setSortDirection;
    if (sortDirection === "ASC") {
      setSortDirection("DESC");
    } else {
      setSortDirection("ASC");
    }
  };
  return (
    <Grid mb={12} className={styles.filterAndSort}>
      <Grid.Col span="content">
        <SortButton
          onClick={() => changeDirection()}
          currentDirection={sortDirection}
          data-testid="contract-sort-direction-button"
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 3 }}>
        <TextInput
          variant="filled"
          placeholder={t("actions:search")}
          rightSection={<SVGAsset name="search" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, xs: 6, lg: 2 }}>
        <Select
          data-testid="contract-status-filter"
          variant={"filled"}
          placeholder={"Status"}
          multiple={false}
          defaultValue={""}
          value={statuses[0] === undefined ? "" : statuses[0]}
          data={[
            {
              value: "",
              label: t("commonComponents:statusX", {
                field: t(`enumerables:contractStatus.unfiltered`),
              }),
            },
            ...statusOptions.map((option: string) => ({
              value: option,
              label: t("commonComponents:statusX", {
                field: t(`enumerables:contractStatus.${option}`),
              }),
            })),
          ]}
          onChange={(val) => {
            reset();
            setStatuses(val ? [val as ContractStatusEnum] : []);
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, xs: 6, lg: 2 }}>
        <Select
          data-testid="contract-sort-field-select"
          variant="filled"
          data={sortOptions}
          value={sortOption}
          onChange={(val) => setSortOption(val as ContractSortTypeEnum)}
          defaultValue={sortOptions.find((option) => option.value === sortOption)?.value}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 4, lg: 2 }}>
        <Select
          placeholder={t("actions:editContracts")}
          variant={"filled"}
          data={[
            { value: "off", label: t("actions:turnOffEdit") },
            { value: "price", label: t("actions:editPrice") },
          ]}
          leftSection={<SVGAsset name={"pencil"} />}
          value={editMode}
          onChange={(val: string | null) => {
            if (val === "off") {
              setEditMode(null);
              clearSelection();
            } else {
              setEditMode("price");
            }
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 6, lg: 2 }}>
        <CTA
          intent={"secondaryGrey"}
          disabled={editMode === null || selectedRows.length === 0}
          onClick={() => openModal()}
        >
          {t("actions:editSelected")}
        </CTA>
      </Grid.Col>
    </Grid>
  );
};

export default ContractsFilter;
